// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useState, useMemo, useCallback, useEffect } from 'react';

import { useDevices } from '../../../context/DeviceContext';
import type { CheckinHistoryEntry, CheckinStation } from '../../../types';
import { checkinAPI } from '../../../utils/apiClient';
import logger from '../../../utils/logger';
import { Button } from '../../common/Button';

interface CheckinHistoryProps {
  stations: CheckinStation[];
}

/** Format a Date to the value expected by <input type="datetime-local"> (local time, no TZ). */
const toLocalInputValue = (d: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

/** Convert a datetime-local string to an ISO timestamp for the backend. */
const inputToIso = (v: string): string => (v ? new Date(v).toISOString() : '');

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const CheckinHistory: React.FC<CheckinHistoryProps> = ({ stations }) => {
  const { devices } = useDevices();

  const initialRange = useMemo(() => {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return { from: toLocalInputValue(yesterday), to: toLocalInputValue(now) };
  }, []);

  const [from, setFrom] = useState<string>(initialRange.from);
  const [to, setTo] = useState<string>(initialRange.to);
  const [deveui, setDeveui] = useState<string>('all');
  const [checkpoint, setCheckpoint] = useState<string>('all');
  const [entries, setEntries] = useState<CheckinHistoryEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const deviceOptions = useMemo(
    () =>
      devices
        .filter((d) => d.deviceCategory === 'lf_device')
        .map((d) => ({ deveui: d.id, label: d.title || d.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [devices]
  );

  const stationOptions = useMemo(
    () =>
      stations
        .map((s) => ({
          deveui: s.deveui || s.device_id,
          label: s.name || s.device_id,
        }))
        .filter((s) => !!s.deveui)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [stations]
  );

  const search = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await checkinAPI.getHistory({
        from: inputToIso(from),
        to: inputToIso(to),
        deveui,
        checkpoint,
      });
      setEntries(res.history || []);
      setHasSearched(true);
    } catch (e) {
      logger.error('Failed to fetch checkin history', e);
      setError((e as Error).message || 'Failed to fetch check-in history');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, deveui, checkpoint]);

  // Run an initial search on mount so the table isn't empty on first view.
  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const { blob, filename } = await checkinAPI.getHistoryCsvBlob({
        from: inputToIso(from),
        to: inputToIso(to),
        deveui,
        checkpoint,
      });
      triggerBlobDownload(blob, filename);
    } catch (e) {
      logger.error('Failed to export checkin history CSV', e);
      setError((e as Error).message || 'CSV export failed');
    } finally {
      setExporting(false);
    }
  }, [from, to, deveui, checkpoint]);

  const inputStyle: React.CSSProperties = {
    padding: '0.8vmin 1vmin',
    fontSize: 'max(12px, 1.6vmin)',
    border: '1px solid #ccc',
    borderRadius: '0.5vmin',
    background: 'white',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3vmin',
    fontSize: 'max(11px, 1.4vmin)',
    color: '#555',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5vmin' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.5vmin',
          padding: '1.5vmin',
          background: '#f8f9fa',
          borderRadius: '1vmin',
          border: '1px solid #e0e0e0',
          alignItems: 'flex-end',
        }}
      >
        <label style={labelStyle}>
          From
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          To
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          Device
          <select
            value={deveui}
            onChange={(e) => setDeveui(e.target.value)}
            style={{ ...inputStyle, minWidth: '22vmin' }}
          >
            <option value="all">All devices</option>
            {deviceOptions.map((d) => (
              <option key={d.deveui} value={d.deveui}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        <label style={labelStyle}>
          Check-in station
          <select
            value={checkpoint}
            onChange={(e) => setCheckpoint(e.target.value)}
            style={{ ...inputStyle, minWidth: '22vmin' }}
          >
            <option value="all">All stations</option>
            {stationOptions.map((s) => (
              <option key={s.deveui} value={s.deveui}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <Button text={loading ? 'Searching…' : 'Search'} onClick={search} disabled={loading} />
        <Button
          text={exporting ? 'Exporting…' : 'Export CSV'}
          onClick={exportCsv}
          disabled={exporting || entries.length === 0}
        />
      </div>

      {error && (
        <div
          style={{
            padding: '1vmin',
            background: '#fdecea',
            color: '#b71c1c',
            border: '1px solid #f5c6cb',
            borderRadius: '0.5vmin',
            fontSize: 'max(12px, 1.6vmin)',
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: 'white',
          borderRadius: '1vmin',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '1vmin 1.5vmin',
            background: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0',
            fontSize: 'max(12px, 1.5vmin)',
            color: '#555',
          }}
        >
          {hasSearched
            ? `${entries.length} check-in${entries.length === 1 ? '' : 's'}`
            : 'Select filters and press Search'}
        </div>
        <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'max(12px, 1.5vmin)' }}
          >
            <thead style={{ position: 'sticky', top: 0, background: '#fafafa' }}>
              <tr>
                <th
                  style={{ textAlign: 'left', padding: '1vmin', borderBottom: '1px solid #e0e0e0' }}
                >
                  Time
                </th>
                <th
                  style={{ textAlign: 'left', padding: '1vmin', borderBottom: '1px solid #e0e0e0' }}
                >
                  Device
                </th>
                <th
                  style={{ textAlign: 'left', padding: '1vmin', borderBottom: '1px solid #e0e0e0' }}
                >
                  Device EUI
                </th>
                <th
                  style={{ textAlign: 'left', padding: '1vmin', borderBottom: '1px solid #e0e0e0' }}
                >
                  Check-in station
                </th>
                <th
                  style={{ textAlign: 'left', padding: '1vmin', borderBottom: '1px solid #e0e0e0' }}
                >
                  Station EUI
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row) => (
                <tr key={row.id}>
                  <td style={{ padding: '1vmin', borderBottom: '1px solid #f0f0f0' }}>
                    {new Date(row.timestamp).toLocaleString()}
                  </td>
                  <td style={{ padding: '1vmin', borderBottom: '1px solid #f0f0f0' }}>
                    {row.device_description || row.device_name || '—'}
                  </td>
                  <td
                    style={{
                      padding: '1vmin',
                      borderBottom: '1px solid #f0f0f0',
                      fontFamily: 'monospace',
                      color: '#666',
                    }}
                  >
                    {row.device}
                  </td>
                  <td style={{ padding: '1vmin', borderBottom: '1px solid #f0f0f0' }}>
                    {row.checkpoint_description || row.checkpoint_name || '—'}
                  </td>
                  <td
                    style={{
                      padding: '1vmin',
                      borderBottom: '1px solid #f0f0f0',
                      fontFamily: 'monospace',
                      color: '#666',
                    }}
                  >
                    {row.checkpoint}
                  </td>
                </tr>
              ))}
              {hasSearched && entries.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      padding: '3vmin',
                      textAlign: 'center',
                      color: '#888',
                      fontStyle: 'italic',
                    }}
                  >
                    No check-ins found for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ fontSize: 'max(11px, 1.3vmin)', color: '#888' }}>
        Results are capped at 5,000 rows. Narrow the time range or use the CSV export for larger
        datasets.
      </div>
    </div>
  );
};

export default CheckinHistory;
